import { Component, ChangeDetectionStrategy, ChangeDetectorRef, type OnInit, type OnDestroy } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { Web3Service } from "../../services/web3.service";
import { PunkWrapperService } from "../../services/punk-wrapper.service";
import { StateService } from "../../services/state.service";

import { truncateAddress } from "../../utils/truncateAddress";
import { CommonModule } from "@angular/common";
import { environment } from "src/environments/environment";
import { NotConnectedComponent } from "../not-connected/not-connected.component";

interface PunkData {
	id: number;
	source: string;
	label: string;
	bgClass: string;
	hoverClass: string;
	textClass: string;
	classes: string;
	selected: boolean;
}

interface CategorizedPunks {
	myPunks: unknown[];
	myProxyPunks: unknown[];
	myStashPunks: unknown[];
	myWrappedPunks: unknown[];
	myC721Tokens: unknown[];
	allPunks: unknown[];
}

@Component({
	selector: "app-v2-wrapper",
	templateUrl: "./wrapper.v2.component.html",
	styleUrls: ["./wrapper.v2.component.scss"],
	imports: [CommonModule, NotConnectedComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WrapperV2Component implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	// State properties
	loading = false;
	error: string | null = null;
	proxy: string | null = null;
	punkId: number | null = null;
	punkOwner: string | null = null;
	isMounted = false;
	allPunksAssignedFlag = false;
	claimingAllPunks = false;
	isConnected = false;

	// C721 specific data
	c721Proxy: string | null = null;
	myC721Tokens: unknown[] = [];
	myPunks: unknown[] = [];
	myProxyPunks: unknown[] = [];
	myStashPunks: unknown[] = [];
	myWrappedPunks: unknown[] = [];
	loadingMyPunks = true;
	selectedPunks: number[] = [];

	// Selection state
	selectedPunkType: string | null = null; // 'unwrapped', 'proxy', 'stash', 'legacy-wrapped', 'c721-wrapped'

	// Tab state
	activeTab = "legacy"; // Default to legacy workflow

	// Data source state
	dataSource = environment.env === "testnet" ? "dev" : "mainnet"; // 'dev' for local blockchain, 'mainnet' for subgraph

	// Transaction loading states
	isTransactionLoading = false;
	currentTransactionType: string | null = null; // 'transfer', 'wrap', 'unwrap', 'register-proxy'

	// Web3 service properties
	user: string | null = null;
	wallet: unknown = null;

	// Cached computed values (rebuilt only when data changes)
	cachedAllPunks: PunkData[] = [];
	cachedHasAnyPunks = false;
	cachedTotalCount = 0;
	private selectedPunksSet = new Set<number>();

	constructor(
		private web3Service: Web3Service,
		private punkWrapperService: PunkWrapperService,
		private stateService: StateService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.isMounted = true;

		this.stateService.walletAddress$
			.pipe(takeUntil(this.destroy$))
			.subscribe((user: string | null) => {
				this.user = user;
				this.isConnected = !!user;
				this.cdr.markForCheck();
				if (user && this.isMounted) {
					this.initializeUserData();
				}
			});

		this.stateService.web3Connected$
			.pipe(takeUntil(this.destroy$))
			.subscribe((connected: boolean) => {
				this.isConnected = connected;
				this.cdr.markForCheck();
			});

		// Listen for custom events to select punks from item-view
		window.addEventListener('selectPunkForWrap', this.handleSelectPunkForWrap as EventListener);

		// Initialize user data if already connected
		if (this.stateService.getWalletAddress()) {
			this.user = this.stateService.getWalletAddress();
			this.isConnected = true;
			this.initializeUserData();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		
		// Remove event listener
		window.removeEventListener('selectPunkForWrap', this.handleSelectPunkForWrap as EventListener);
	}

	private async initializeUserData(): Promise<void> {
		if (!this.user) return;

		try {
			// Load proxy info first, then query punks
			await Promise.all([
				this.getProxyInfo(),
				this.getPunkProxy(),
				this.checkAllPunksAssigned(),
			]);

			// Query punks after proxy info is loaded
			await this.queryMyPunks();
		} catch (error) {
			console.error("Error initializing user data:", error);
		}
	}

	// Cached permission flags (rebuilt via refreshSelectionState)
	hasAnyPunks = false;
	allPunksClaimed = false;
	selectedGroup: string | null = null;
	validLegacyProxy = false;
	canTransferToStash = false;
	canWrapC721 = false;
	canUnwrapC721 = false;
	canTransferToProxy = false;
	canWrapLegacy = false;
	canUnwrapLegacy = false;
	canUseLegacyWorkflow = false;
	canUseC721Workflow = false;

	/**
	 * Rebuild the flat allPunks list + derived flags.
	 * Call this whenever myPunks / myProxyPunks / ... change.
	 */
	private rebuildAllPunks(): void {
		const format = (
			ids: unknown[],
			group: string,
			label: string,
			bgClass: string,
			hoverClass: string,
			textClass: string,
		): PunkData[] =>
			(ids || []).map((p) => {
				const id =
					typeof p === "object" && p && "id" in p
						? (p as { id: number }).id
						: (p as number);
				const selected = this.selectedPunksSet.has(id);
				const filtered = !!this.selectedGroup && group !== this.selectedGroup;
				const selectedClass = selected ? "punk-selected" : bgClass;
				const filteredClass = filtered ? "filtered-punk" : "border-default";
				return {
					id,
					source: group,
					label,
					bgClass,
					hoverClass,
					textClass,
					selected,
					classes: `punk-base ${selectedClass} ${hoverClass} ${filteredClass}`,
				};
			});

		this.cachedAllPunks = [
			...format(this.myPunks, "unwrapped", "Unwrapped", "bg-stone", "hover-stone", "text-stone"),
			...format(this.myProxyPunks, "proxy", "In Proxy", "bg-orange", "hover-orange", "text-orange"),
			...format(this.myStashPunks, "stash", "In Stash", "bg-purple", "hover-purple", "text-purple"),
			...format(this.myWrappedPunks, "legacy-wrapped", "Legacy", "bg-yellow", "hover-yellow", "text-yellow"),
			...format(this.myC721Tokens, "c721-wrapped", "C721", "bg-green", "hover-green", "text-green"),
		];

		this.cachedHasAnyPunks = !!(this.myPunks.length || this.myProxyPunks.length || this.myStashPunks.length || this.myWrappedPunks.length || this.myC721Tokens.length);
		this.hasAnyPunks = this.cachedHasAnyPunks;
		this.cachedTotalCount = this.myPunks.length + this.myProxyPunks.length + this.myStashPunks.length + this.myWrappedPunks.length + this.myC721Tokens.length;
		this.allPunksClaimed = this.allPunksAssignedFlag;
	}

	/**
	 * Refresh selection-dependent state (permissions, classes).
	 * Call this whenever selectedPunks / selectedPunkType change.
	 */
	private refreshSelectionState(): void {
		this.selectedPunksSet = new Set(this.selectedPunks);
		this.selectedGroup = this.selectedPunks.length ? this.selectedPunkType : null;

		const validProxy = this.isValidProxy(this.proxy);
		this.validLegacyProxy = validProxy;
		this.canTransferToStash = this.selectedPunkType === "unwrapped" && this.selectedPunks.length > 0;
		this.canWrapC721 = this.selectedPunkType === "stash" && this.selectedPunks.length > 0;
		this.canUnwrapC721 = this.selectedPunkType === "c721-wrapped" && this.selectedPunks.length > 0;
		this.canTransferToProxy = this.selectedPunkType === "unwrapped" && this.selectedPunks.length === 1 && validProxy;
		this.canWrapLegacy = this.selectedPunkType === "proxy" && this.selectedPunks.length === 1 && validProxy;
		this.canUnwrapLegacy = this.selectedPunkType === "legacy-wrapped" && this.selectedPunks.length === 1 && validProxy;
		this.canUseLegacyWorkflow = this.canTransferToProxy || this.canWrapLegacy || this.canUnwrapLegacy;
		this.canUseC721Workflow = this.canTransferToStash || this.canWrapC721 || this.canUnwrapC721;

		this.rebuildAllPunks();
		this.cdr.markForCheck();
	}

	// Methods
	async checkAllPunksAssigned(): Promise<void> {
		if (!this.isMounted || !this.user) return;
		const chainId = this.punkWrapperService.getChainId();
		try {
			const allAssigned = await this.punkWrapperService.allPunksAssigned(this.user, chainId);
			this.allPunksAssignedFlag = allAssigned;
			console.log("All punks assigned status:", allAssigned);
		} catch (err) {
			console.log({ checkAllPunksAssignedErr: err });
			this.allPunksAssignedFlag = false;
		}
	}

	async getProxyInfo(): Promise<void> {
		if (!this.isMounted || !this.user) return;
		try {
			const proxy = await this.punkWrapperService.getProxyInfo(this.user);
			this.proxy = proxy;
		} catch (err) {
			console.log({ getProxyInfoErr: err });
		}
	}

	async registerProxyData(): Promise<void> {
		if (!this.isMounted || !this.user) return;
		const chainId = this.punkWrapperService.getChainId();

		this.isTransactionLoading = true;
		this.currentTransactionType = "register-proxy";
		this.cdr.markForCheck();

		try {
			const tx = await this.punkWrapperService.registerProxy(
				this.user,
				chainId,
				async (tx: unknown) => {
					console.log("Proxy registration completed:", tx);
					await this.getProxyInfo();
					this.refreshSelectionState();
				},
			);
			console.log({ tx });
		} catch (err) {
			console.log({ registerProxyErr: err });
		} finally {
			this.isTransactionLoading = false;
			this.currentTransactionType = null;
			this.cdr.markForCheck();
		}
	}

	async getAllPunks(): Promise<void> {
		if (!this.isMounted || !this.user) return;

		this.claimingAllPunks = true;
		this.cdr.markForCheck();

		try {
			const chainId = this.punkWrapperService.getChainId();
			await this.punkWrapperService.initializeAllPunks(this.user, chainId);
			await this.checkAllPunksAssigned();

			if (this.allPunksAssignedFlag) {
				await this.queryMyPunks();
			}
		} catch (error) {
			console.error("Error in getAllPunks:", error);
			this.allPunksAssignedFlag = false;
		} finally {
			this.claimingAllPunks = false;
			this.cdr.markForCheck();
		}
	}

	async transferPunkToProxy(): Promise<void> {
		if (!this.isMounted || !this.selectedPunks.length || !this.user) return;

		if (!this.isValidProxy(this.proxy)) {
			alert("Proxy missing - please register proxy first");
			return;
		}

		const chainId = this.punkWrapperService.getChainId();
		const punkId = this.selectedPunks[0];

		try {
			const owner = await this.punkWrapperService.punkIndexToAddress(this.user, chainId, punkId);
			if (owner.toLowerCase() !== this.user.toLowerCase()) {
				alert(`You don't own Punk #${punkId}. It belongs to ${owner}`);
				return;
			}
		} catch (err) {
			console.log({ checkOwnerErr: err });
			return;
		}

		this.isTransactionLoading = true;
		this.currentTransactionType = "transfer";
		this.cdr.markForCheck();

		console.log(
			`owner: ${this.user}, chainId: ${chainId}, punkIndex: ${punkId}, proxy: ${this.proxy}`,
		);
		try {
			const tx = await this.punkWrapperService.transferPunk(
				this.user,
				chainId,
				this.proxy || "",
				punkId,
				async (tx: unknown) => {
					console.log("Transfer transaction completed:", tx);
					this.selectedPunks = [];
					this.selectedPunkType = null;
					await this.queryMyPunks();
				},
			);
		} catch (err) {
			console.log({ transferPunkErr: err });
		} finally {
			this.isTransactionLoading = false;
			this.currentTransactionType = null;
			this.cdr.markForCheck();
		}
	}

	async mintWrappedPunkBt(): Promise<void> {
		if (!this.isMounted || !this.selectedPunks.length || !this.user) return;

		const chainId = this.punkWrapperService.getChainId();
		const punkId = this.selectedPunks[0];

		if (!this.isValidProxy(this.proxy)) {
			alert("You need to register a proxy first");
			return;
		}

		try {
			const isWrapped = await this.punkWrapperService.isPunkWrapped(this.user, chainId, punkId);
			if (isWrapped) {
				alert(`Punk #${punkId} is already wrapped.`);
				return;
			}
		} catch (err) {
			console.log({ checkWrappedErr: err });
		}

		this.isTransactionLoading = true;
		this.currentTransactionType = "wrap";
		this.cdr.markForCheck();

		try {
			console.log(
				`owner: ${this.user}, chainId: ${chainId}, punkIndex: ${punkId}`,
			);
			await this.punkWrapperService.mintWrappedPunk(this.user, chainId, punkId, async (tx: unknown) => {
				console.log("Mint transaction completed:", tx);
				this.selectedPunks = [];
				this.selectedPunkType = null;
				await this.queryMyPunks();
			});
		} catch (err) {
			console.log({ mintWrappedPunkErr: err });
		} finally {
			this.isTransactionLoading = false;
			this.currentTransactionType = null;
			this.cdr.markForCheck();
		}
	}

	async burnWrappedPunkBt(): Promise<void> {
		if (!this.isMounted || !this.selectedPunks.length || !this.user) return;

		const chainId = this.punkWrapperService.getChainId();
		const punkId = this.selectedPunks[0];

		try {
			const isWrapped = await this.punkWrapperService.isPunkWrapped(this.user, chainId, punkId);
			if (!isWrapped) {
				alert(`Punk #${punkId} is not wrapped. Please wrap it first.`);
				return;
			}
		} catch (err) {
			console.log({ checkWrappedErr: err });
			return;
		}

		this.isTransactionLoading = true;
		this.currentTransactionType = "unwrap";
		this.cdr.markForCheck();

		try {
			console.log(`Burning wrapped punk #${punkId}`);
			await this.punkWrapperService.burnWrappedPunk(this.user, chainId, punkId, async (tx: unknown) => {
				console.log("Burn transaction completed:", tx);
				console.log(`Successfully burned wrapped punk #${punkId}`);
				this.selectedPunks = [];
				this.selectedPunkType = null;
				await this.queryMyPunks();
			});
		} catch (err) {
			console.log({ burnWrappedPunkErr: err });
		} finally {
			this.isTransactionLoading = false;
			this.currentTransactionType = null;
			this.cdr.markForCheck();
		}
	}

	// C721 Contract Methods
	async getPunkProxy(): Promise<void> {
		if (!this.isMounted || !this.user) return;
		const chainId = this.punkWrapperService.getChainId();
		try {
			const proxy = await this.punkWrapperService.getPunkProxyForUser(this.user);
			this.c721Proxy = proxy;
			console.log(`C721 Proxy address for ${this.user} is ${proxy}`);
		} catch (err) {
			console.log({ getPunkProxyErr: err });
			alert(`Error getting C721 proxy: ${err}`);
		}
	}

	async transferToStashUnified(): Promise<void> {
		if (!this.isMounted || !this.selectedPunks.length || !this.user) return;

		const chainId = this.punkWrapperService.getChainId();

		this.isTransactionLoading = true;
		this.currentTransactionType = "transfer";
		this.cdr.markForCheck();

		try {
			if (this.selectedPunks.length === 1) {
				await this.punkWrapperService.transferPunkToStash(
					this.user,
					chainId,
					this.selectedPunks[0],
					async (result: unknown) => {
						console.log("C721 Transfer to stash completed:", result);
						this.selectedPunks = [];
						this.selectedPunkType = null;
						await this.queryMyPunks();
					},
				);
			} else {
				await this.punkWrapperService.transferPunkBatchToStash(
					this.user,
					chainId,
					this.selectedPunks,
					async (result: unknown) => {
						console.log("C721 Batch transfer to stash completed:", result);
						this.selectedPunks = [];
						this.selectedPunkType = null;
						await this.queryMyPunks();
					},
				);
			}
		} catch (err) {
			console.log({ transferToStashUnifiedErr: err });
			alert(`Error transferring punks to stash: ${err}`);
		} finally {
			this.isTransactionLoading = false;
			this.currentTransactionType = null;
			this.cdr.markForCheck();
		}
	}

	async wrapUnified(): Promise<void> {
		if (!this.isMounted || !this.selectedPunks.length || !this.user) return;

		const chainId = this.punkWrapperService.getChainId();

		this.isTransactionLoading = true;
		this.currentTransactionType = "wrap";
		this.cdr.markForCheck();

		try {
			if (this.selectedPunks.length === 1) {
				await this.punkWrapperService.wrapPunk(
					this.user,
					chainId,
					this.selectedPunks[0],
					async (tx: unknown) => {
						console.log("C721 Wrap transaction completed:", tx);
						this.selectedPunks = [];
						this.selectedPunkType = null;
						await this.queryMyPunks();
					},
				);
			} else {
				await this.punkWrapperService.wrapPunkBatch(
					this.user,
					chainId,
					this.selectedPunks,
					async (tx: unknown) => {
						console.log("C721 Batch Wrap transaction completed:", tx);
						this.selectedPunks = [];
						this.selectedPunkType = null;
						await this.queryMyPunks();
					},
				);
			}
		} catch (err) {
			console.log({ wrapUnifiedErr: err });
			alert(`Error wrapping punks with C721: ${err}`);
		} finally {
			this.isTransactionLoading = false;
			this.currentTransactionType = null;
			this.cdr.markForCheck();
		}
	}

	async unwrapUnified(): Promise<void> {
		if (!this.isMounted || !this.selectedPunks.length || !this.user) return;

		const chainId = this.punkWrapperService.getChainId();

		this.isTransactionLoading = true;
		this.currentTransactionType = "unwrap";
		this.cdr.markForCheck();

		try {
			if (this.selectedPunks.length === 1) {
				await this.punkWrapperService.unwrapPunk(
					this.user,
					chainId,
					this.selectedPunks[0],
					async (tx: unknown) => {
						console.log("C721 Unwrap transaction completed:", tx);
						this.selectedPunks = [];
						this.selectedPunkType = null;
						await this.queryMyPunks();
					},
				);
			} else {
				await this.punkWrapperService.unwrapPunkBatch(
					this.user,
					chainId,
					this.selectedPunks,
					async (tx: unknown) => {
						console.log("C721 Batch Unwrap transaction completed:", tx);
						this.selectedPunks = [];
						this.selectedPunkType = null;
						await this.queryMyPunks();
					},
				);
			}
		} catch (err) {
			console.log({ unwrapUnifiedErr: err });
			alert(`Error unwrapping punks with C721: ${err}`);
		} finally {
			this.isTransactionLoading = false;
			this.currentTransactionType = null;
			this.cdr.markForCheck();
		}
	}

	async getTokensOfOwner(): Promise<void> {
		if (!this.isMounted || !this.user) return;
		const chainId = this.punkWrapperService.getChainId();

		try {
			const tokens = await this.punkWrapperService.getTokensOfOwner(this.user, chainId, this.user);
			this.myC721Tokens = tokens.map((token: unknown) => String(token));
		} catch (err) {
			console.log({ getTokensOfOwnerErr: err });
			alert(`Error getting tokens: ${err}`);
		}
	}

	async queryMyPunks(): Promise<void> {
		if (!this.isMounted || !this.user) return;
		this.loadingMyPunks = true;
		this.cdr.markForCheck();

		try {
			if (this.dataSource === "mainnet") {
				await this.queryPunksFromMainnet();
			} else {
				await this.queryPunksFromDev();
			}
		} catch (err) {
			console.log({ queryMyPunksErr: err });
			alert(`Error querying my punks: ${err}`);
		} finally {
			this.loadingMyPunks = false;
			this.refreshSelectionState();
		}
	}

	async queryPunksFromDev(): Promise<void> {
		if (!this.user) return;

		const chainId = this.punkWrapperService.getChainId();
		const punkIds = Array.from({ length: 50 }, (_, i) => i);

		// Build promises array with conditional queries
		const promises = [
			this.punkWrapperService.queryPunksOwnersLocal(this.user, chainId, punkIds),
			this.punkWrapperService.getWrappedPunksOfOwner(this.user, chainId),
		];

		// Only query proxy punks if we have a valid proxy address
		const proxyPromise =
			this.isValidProxy(this.proxy) && this.proxy
				? this.punkWrapperService.queryPunksOwnersLocal(this.proxy, chainId, punkIds)
				: Promise.resolve([]);

		// Only query stash punks if we have a valid c721 proxy address
		const stashPromise =
			this.isValidProxy(this.c721Proxy) && this.c721Proxy
				? this.punkWrapperService.queryPunksOwnersLocal(this.c721Proxy, chainId, punkIds)
				: Promise.resolve([]);

		promises.push(proxyPromise, stashPromise);

		const [myPunks, myWrappedPunks, myProxyPunks, myStashPunks] =
			await Promise.all(promises);

		this.myPunks = myPunks;
		this.myProxyPunks = myProxyPunks;
		this.myStashPunks = myStashPunks;
		this.myWrappedPunks = myWrappedPunks;

		await this.getTokensOfOwner();
	}

	async queryPunksFromMainnet(): Promise<void> {
		if (!this.user) return;

		try {
			// Wait for proxy info and C721 proxy info to be available
			await Promise.all([this.getProxyInfo(), this.getPunkProxy()]);

			console.log(`Querying subgraph for user: ${this.user}`);
			console.log(`Legacy proxy: ${this.proxy || "None"}`);
			console.log(`C721 stash: ${this.c721Proxy || "None"}`);

			const categorizedPunks = (await this.punkWrapperService.queryPunksFromSubgraph(
				this.user, // Utiliser l'adresse réelle du user
				this.proxy || "", // Utiliser l'adresse réelle du proxy
				this.c721Proxy || "", // Utiliser l'adresse réelle du c721 proxy
			)) as CategorizedPunks;

			this.myPunks = categorizedPunks.myPunks;
			this.myProxyPunks = categorizedPunks.myProxyPunks;
			this.myStashPunks = categorizedPunks.myStashPunks;
			this.myWrappedPunks = categorizedPunks.myWrappedPunks;
			this.myC721Tokens = categorizedPunks.myC721Tokens;

			console.log("Loaded punks from mainnet subgraph:", categorizedPunks);
			console.log(
				`Found ${categorizedPunks.allPunks?.length || 0} total punks for user and associated addresses`,
			);
		} catch (error) {
			console.error("Error querying mainnet subgraph:", error);
			throw error;
		}
	}

	selectPunk(id: number, type: string): void {
		if (this.selectedPunkType && this.selectedPunkType !== type) {
			this.selectedPunks = [id];
			this.selectedPunkType = type;
		} else {
			const index = this.selectedPunks.indexOf(id);
			if (index > -1) {
				this.selectedPunks.splice(index, 1);
				if (this.selectedPunks.length === 0) {
					this.selectedPunkType = null;
				}
			} else {
				this.selectedPunks.push(id);
				this.selectedPunkType = type;
			}
		}

		this.refreshSelectionState();
	}

	truncate(address: string | null): string {
		return truncateAddress(address);
	}

	isValidProxy(proxy: string | null): boolean {
		return this.punkWrapperService.isValidProxy(proxy || "");
	}

	trackByPunk(index: number, punk: PunkData): string {
		return `${punk.source}-${punk.id}`;
	}

	async switchDataSource(source: string): Promise<void> {
		if (this.dataSource === source) return;

		this.dataSource = source;
		console.log(`Switched data source to: ${source}`);

		this.selectedPunks = [];
		this.selectedPunkType = null;

		await this.queryMyPunks();
	}

	get isMainnetMode(): boolean {
		return this.dataSource === "mainnet";
	}

	get isDevMode(): boolean {
		return this.dataSource === "dev";
	}

	get currentDataSourceLabel(): string {
		if (this.dataSource === "mainnet") {
			return "Mainnet (Subgraph)";
		}
		return "Local Dev";
	}

	/**
	 * Handle custom event to select a punk for wrap/unwrap operations
	 */
	private handleSelectPunkForWrap = (event: CustomEvent): void => {
		const { punkId, isWrapped } = event.detail;
		
		console.log(`[WrapperV2Component] Received selectPunkForWrap event for punk ${punkId}, isWrapped: ${isWrapped}`);
		
		// Wait for the component to be fully loaded
		if (!this.isMounted || !this.user) {
			console.warn('[WrapperV2Component] Component not ready for punk selection');
			return;
		}
		
		// If data is still loading, wait for it to complete
		if (this.loadingMyPunks) {
			console.log('[WrapperV2Component] Data still loading, waiting for completion...');
			this.waitForDataAndSelectPunk(punkId, isWrapped);
			return;
		}
		
		this.selectPunkWithType(punkId, isWrapped);
	};

	/**
	 * Wait for data to load and then select the punk
	 */
	private waitForDataAndSelectPunk(punkId: number, isWrapped: boolean): void {
		const checkData = () => {
			if (!this.loadingMyPunks) {
				this.selectPunkWithType(punkId, isWrapped);
			} else {
				setTimeout(checkData, 500);
			}
		};
		
		checkData();
	}

	/**
	 * Select punk with the appropriate type
	 */
	private selectPunkWithType(punkId: number, isWrapped: boolean): void {
		// Determine the punk type based on wrapped status
		let punkType: string;
		if (isWrapped) {
			// Check if it's C721 wrapped or legacy wrapped
			punkType = this.myC721Tokens.includes(punkId) ? 'c721-wrapped' : 'legacy-wrapped';
		} else {
			// Check if it's in stash, proxy, or unwrapped
			if (this.myStashPunks.includes(punkId)) {
				punkType = 'stash';
			} else if (this.myProxyPunks.includes(punkId)) {
				punkType = 'proxy';
			} else {
				punkType = 'unwrapped';
			}
		}
		
		// Select the punk
		this.selectPunk(punkId, punkType);
		
		// Switch to the appropriate tab based on the punk type
		if (punkType === 'c721-wrapped' || punkType === 'stash') {
			this.activeTab = 'c721';
		} else {
			this.activeTab = 'legacy';
		}
		
		console.log(`[WrapperV2Component] Selected punk ${punkId} with type ${punkType}, switched to ${this.activeTab} tab`);
	}

	// Connection method
	connect(): void {
		if (this.stateService.getWeb3Connected()) {
			this.web3Service.disconnectWeb3();
		} else {
			this.web3Service.connect();
		}
	}
}
